import React, { useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { CircularProgress } from "@mui/material";
import Modal from "@mui/material/Modal";
import { usePostDeleteSummary } from "api/apiPostDeleteSummary";

import "../../../../css/modals.css";
import { AlertColor } from "../../../../util/constants";
import { DocumentData } from "../../../../util/interfaces/DocumentData";
import { FileData } from "../../../../util/interfaces/FileData";
import { Alerts } from "../../Alerts/Alerts";
import { getDocumentName } from "../DeleteModal/DeleteModal.Utils";
import { createUploadRequest } from "../UploadFile/UploadFile";

import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import { usePostUploadFileOld } from "@/api/usePostUploadFileOld";
import { formatDateTime } from "@/utils/dateUtils";
import { getUserInfo } from "@/utils/session";

interface OverrideModalProps {
  callbackClose: (_updatedFile: DocumentData | null) => void;
  file: DocumentData;
}

interface ISteps {
  number: number;
  subtitle: string;
  body: React.ReactNode;
}

export const OverrideModal: React.FC<OverrideModalProps> = ({ callbackClose, file }) => {
  const { mutateAsync: callDeleteSummary } = usePostDeleteSummary();
  const [files, setFiles] = useState<FileData[]>([]);
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const { mutateAsync: callUploadFile } = usePostUploadFileOld();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disabledNext, setDisabledNext] = useState<boolean>(true);
  const STEPS: ISteps[] = [
    {
      number: 1,
      subtitle:
        'Drag and drop your file into this area to upload or select "Browse Files" to select from you desktop. The uploaded file will permanently replace the existing document',
      body: (
        <StepOne
          callbackError={(msg, alertColor, disabledNext) => {
            setAlertColor(alertColor);
            setAlertMessage(msg);
            setDisabledNext(disabledNext);
          }}
          existingFile={files}
          currentFile={file}
          callbackSetFiles={(files) => {
            setFiles(
              files.map((f) => ({
                file: f.file,
                fileCategory: f.fileCategory ?? "",
                metadata: f.metadata ?? "",
                isCompleted: f.isCompleted ?? false,
                override: f.override ?? true,
                isChecked: false,
              }))
            );
            setDisabledNext(files.length === 0);
          }}
        />
      ),
    },
    {
      number: 2,
      subtitle:
        'Select document on the left-hand side then add document details on the right. click "Save" when completed for all file attachments.',
      body: (
        <StepTwo
          files={files}
          currentFile={file}
          callbackSetMessage={(message: string) => {
            setAlertColor(AlertColor.Success);
            setAlertMessage(message);
          }}
          callbackSetFiles={(processedFiles: FileData[]) => {
            setDisabledNext(false);
            setFiles(processedFiles);
          }}
        />
      ),
    },
  ];

  const [selectedStep, setSelectedStep] = useState<ISteps>(STEPS[0]);

  const getTextOfButton = () => {
    if (selectedStep.number === 2) {
      if (isLoading) {
        return (
          <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
            <CircularProgress size={24} />
            <span style={{ marginLeft: 10 }}>Uploading...</span>
          </div>
        );
      }
      return "Save";
    }
    return "Next";
  };

  const uploadFiles = async () => {
    try {
      const filesToUpload: FileData[] = files.map((item) => ({
        file: new File([item.file], file.documentName, {
          type: item.file.type,
          lastModified: item.file.lastModified,
        }),
        fileCategory: item.fileCategory,
        metadata: item.metadata,
        path: file.path,
        uuid: file.uuid,
      }));

      const sendData = createUploadRequest(filesToUpload);
      const response = await callUploadFile(sendData);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
    } catch {
      setAlertColor(AlertColor.Error);
      setAlertMessage("Unexpected error while trying to upload files into the storage.");
      setIsLoading(false);
      setDisabledNext(false);
      return false;
    }
    return true;
  };

  const deleteSummary = async () => {
    try {
      if (files.length === 0) {
        throw new Error("El arreglo de documentos está vacío");
      }

      const response = await callDeleteSummary(getDocumentName(file.fileAlias));
      if (response.status != 200) {
        throw new Error();
      }

      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setDisabledNext(true);

    try {
      const deleteResult = await deleteSummary();
      if (!deleteResult) {
        throw new Error("Error while deleting summary.");
      }

      const uploadResult = await uploadFiles();
      if (!uploadResult) {
        throw new Error("Error while uploading files to the system.");
      }
      const { nameUser } = getUserInfo();
      callbackClose(
        files.map((item) => ({
          ...file,
          category: item.fileCategory,
          metadata: item.metadata,
          uploadedBy: nameUser,
          uploaded: formatDateTime(new Date()),
        }))[0]
      );
    } catch (error: any) {
      setAlertColor(AlertColor.Error);
      setAlertMessage(error.message || "Unexpected error during submission.");
    } finally {
      setIsLoading(false);
      setDisabledNext(false);
    }
  };

  const getTextOfCancelButton = (number: number) => {
    switch (number) {
      case 1:
        return "Cancel";
      case 2:
        return "Previous";
      default:
        return "Cancel";
    }
  };

  return (
    <>
      <Modal open={true} onClose={() => callbackClose(null)}>
        <div
          className="modal-kb-content"
          style={{
            height: files.length === 0 ? "70vh" : "75vh",
            width: "60vw",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "4px",
          }}
          data-testid="modal_edit"
        >
          <div className="modal-kb-header">
            <button onClick={() => callbackClose(null)} className="close_icon">
              <Icon icon="bitcoin-icons:cross-filled" height={24} color="black" />
            </button>
            <div className="flex flow-column" style={{ gap: "15px" }}>
              <span className="override-modal-title">Update {file.documentName} version</span>
              <span style={{ fontSize: "14px" }}>{selectedStep.subtitle}</span>
            </div>
          </div>
          <div className="modal-kb-body override_modal_content" style={{ overflowY: "auto" }}>
            {selectedStep.body}
          </div>
          <div className="modal-kb-footer">
            <button
              className="save_btn"
              data-testid="nextStepButton"
              onClick={() => {
                if (selectedStep.number === 2) {
                  handleSubmit();
                } else {
                  setSelectedStep(STEPS[selectedStep.number]);
                }
              }}
              disabled={disabledNext}
            >
              {getTextOfButton()}
            </button>
            <button
              className="cancel_btn"
              onClick={() => {
                if (selectedStep.number === 2) {
                  setSelectedStep(STEPS[selectedStep.number - 2]);
                } else {
                  callbackClose(null);
                }
              }}
              disabled={isLoading}
            >
              {getTextOfCancelButton(selectedStep.number)}
            </button>
          </div>
        </div>
      </Modal>
      <Alerts
        style={{ top: "", right: "", left: "" }}
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
